import React from 'react';
import { Loader2, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const VoiceNoteTasksEditor = ({ 
  tasks,
  users,
  companies,
  opportunities,
  typsaOrgUnits,
  onUpdateTask,
  onRemoveTask,
  onCreateTasks,
  creatingTasks,
  isEditor 
}) => {
  if (!tasks || tasks.length === 0) return null;

  const getFilteredCompanies = (task) => {
    const companyType = task._companyType || '';
    if (!companyType || companyType === 'typsa') return [];
    return companies.filter(c => c.company_type === companyType);
  };

  const getFilteredTypsaUnits = (task) => {
    const category = task._typsaCategory || '';
    if (!category) return [];
    const normalizedCategory = category.toLowerCase().trim();
    return typsaOrgUnits.filter(u => 
      u.category && u.category.toLowerCase().trim() === normalizedCategory
    );
  };

  const getFilteredOpportunities = (task) => {
    if (task.typsa_org_unit_id) {
      return opportunities.filter(o => o.typsa_org_unit_id === task.typsa_org_unit_id);
    }
    if (task.company_id) {
      return opportunities.filter(o => o.company_id === task.company_id);
    }
    return [];
  };

  return (
    <Card data-testid="tasks-editor-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Tareas Extraídas ({tasks.length})</CardTitle>
          {isEditor && (
            <Button
              onClick={onCreateTasks}
              disabled={creatingTasks}
              data-testid="create-tasks-btn"
            >
              {creatingTasks ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creando...
                </>
              ) : (
                'Crear Tareas'
              )}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {tasks.map((task, index) => (
          <div 
            key={index} 
            className="border rounded-lg p-4 space-y-3" 
            data-testid={`task-item-${index}`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1 space-y-3">
                {/* Title */}
                <div>
                  <Label>Título</Label>
                  <Input
                    value={task.title}
                    onChange={(e) => onUpdateTask(index, 'title', e.target.value)}
                    className="mt-1"
                    data-testid={`task-title-${index}`}
                  />
                </div>

                {/* Description */}
                <div>
                  <Label>Descripción</Label>
                  <Textarea
                    value={task.brief_description || ''}
                    onChange={(e) => onUpdateTask(index, 'brief_description', e.target.value)}
                    className="mt-1 min-h-[60px]"
                    placeholder="Contexto adicional..."
                    data-testid={`task-description-${index}`}
                  />
                </div>

                {/* Row: Assignee, Due Date, Priority */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <Label>Responsable</Label>
                    <Select
                      value={task.assignee_email || "none"}
                      onValueChange={(value) => {
                        const selectedUser = value === "none" ? null : users.find(u => u.email === value);
                        onUpdateTask(index, 'assignee_email', selectedUser ? selectedUser.email : null);
                        onUpdateTask(index, 'owner', selectedUser ? selectedUser.name : null);
                      }}
                    >
                      <SelectTrigger className="mt-1" data-testid={`task-assignee-${index}`}>
                        <SelectValue placeholder="Seleccionar..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin asignar</SelectItem>
                        {users.map((user) => (
                          <SelectItem key={user.email} value={user.email}>
                            {user.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Fecha vencimiento</Label>
                    <Input
                      type="date"
                      value={task.due_date || ''}
                      onChange={(e) => onUpdateTask(index, 'due_date', e.target.value)}
                      className="mt-1"
                      data-testid={`task-due-date-${index}`}
                    />
                  </div>

                  <div>
                    <Label>Prioridad</Label>
                    <Select
                      value={task.priority || "none"}
                      onValueChange={(value) => onUpdateTask(index, 'priority', value === "none" ? null : value)}
                    >
                      <SelectTrigger className="mt-1" data-testid={`task-priority-${index}`}>
                        <SelectValue placeholder="Seleccionar..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin definir</SelectItem>
                        <SelectItem value="baja">Baja</SelectItem>
                        <SelectItem value="media">Media</SelectItem>
                        <SelectItem value="alta">Alta</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Company Type Selection */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Tipo de empresa</Label>
                    <Select
                      value={task._companyType || "none"}
                      onValueChange={(value) => {
                        onUpdateTask(index, '_companyType', value === "none" ? '' : value);
                        onUpdateTask(index, '_typsaCategory', '');
                        onUpdateTask(index, 'company_id', '');
                        onUpdateTask(index, 'typsa_org_unit_id', '');
                        onUpdateTask(index, 'opportunity_id', '');
                      }}
                    >
                      <SelectTrigger className="mt-1" data-testid={`task-company-type-${index}`}>
                        <SelectValue placeholder="Seleccionar..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">-- Seleccionar --</SelectItem>
                        <SelectItem value="typsa">TYPSA</SelectItem>
                        <SelectItem value="privada">Privada</SelectItem>
                        <SelectItem value="publica">Pública</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {task._companyType === 'typsa' && (
                    <div>
                      <Label>Categoría TYPSA</Label>
                      <Select
                        value={task._typsaCategory || "none"}
                        onValueChange={(value) => {
                          onUpdateTask(index, '_typsaCategory', value === "none" ? '' : value);
                          onUpdateTask(index, 'typsa_org_unit_id', '');
                        }}
                      >
                        <SelectTrigger className="mt-1" data-testid={`task-typsa-category-${index}`}>
                          <SelectValue placeholder="Seleccionar..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">-- Seleccionar --</SelectItem>
                          <SelectItem value="division">División</SelectItem>
                          <SelectItem value="departamento">Departamento</SelectItem>
                          <SelectItem value="territorio">Territorio</SelectItem>
                          <SelectItem value="grupo">Grupo</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                {/* Company or TYPSA Unit Selection */}
                {task._companyType && task._companyType !== 'typsa' && (
                  <div>
                    <Label>Empresa</Label>
                    <Select
                      value={task.company_id || "none"}
                      onValueChange={(value) => {
                        onUpdateTask(index, 'company_id', value === "none" ? '' : value);
                        onUpdateTask(index, 'opportunity_id', '');
                      }}
                    >
                      <SelectTrigger className="mt-1" data-testid={`task-company-${index}`}>
                        <SelectValue placeholder="Seleccionar empresa..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">-- Seleccionar --</SelectItem>
                        {getFilteredCompanies(task).map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {task._companyType === 'typsa' && task._typsaCategory && (
                  <div>
                    <Label>Unidad TYPSA</Label>
                    <Select
                      value={task.typsa_org_unit_id || "none"}
                      onValueChange={(value) => {
                        onUpdateTask(index, 'typsa_org_unit_id', value === "none" ? '' : value);
                        onUpdateTask(index, 'opportunity_id', '');
                      }}
                    >
                      <SelectTrigger className="mt-1" data-testid={`task-typsa-unit-${index}`}>
                        <SelectValue placeholder="Seleccionar unidad..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">-- Seleccionar --</SelectItem>
                        {getFilteredTypsaUnits(task).map((u) => (
                          <SelectItem key={u.id} value={u.id}>{u.value}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Opportunity Selection */}
                {(task.company_id || task.typsa_org_unit_id) && (
                  <div>
                    <Label>Oportunidad (opcional)</Label>
                    <Select
                      value={task.opportunity_id || "none"}
                      onValueChange={(value) => onUpdateTask(index, 'opportunity_id', value === "none" ? '' : value)}
                    >
                      <SelectTrigger className="mt-1" data-testid={`task-opportunity-${index}`}>
                        <SelectValue placeholder="Seleccionar..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">-- Sin oportunidad --</SelectItem>
                        {getFilteredOpportunities(task).map((o) => (
                          <SelectItem key={o.id} value={o.id}>{o.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {/* Remove button */}
              {isEditor && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onRemoveTask(index)}
                  className="text-red-500 hover:text-red-700 ml-2"
                  data-testid={`remove-task-${index}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default VoiceNoteTasksEditor;
